import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Save, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

import {
  createCustomCategory,
  getCustomCategories,
  deleteCustomCategory,
} from '../../api/customCategory';

export default function CategoriaCustom({ userId }) {
  const { t, i18n } = useTranslation();

  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newText, setNewText] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function loadCategories() {
      if (!userId) return;
      try {
        setLoading(true);
        const data = await getCustomCategories(userId);
        setCategories(data || []);
      } catch (error) {
        console.error(error);
        toast.error(t('errorLoadingCategories'));
      } finally {
        setLoading(false);
      }
    }
    loadCategories();
  }, [userId, i18n.language, t]);

  function onCancelar() {
    setShowAdd(false);
    setNewText('');
  }

  async function onSalvar() {
    const descricaoOriginal = newText.trim();
    const descricaoNormalizada = descricaoOriginal.toLowerCase();

    if (!descricaoOriginal) {
      toast.error(t('fillDescriptionBeforeSaving'));
      return;
    }

    const categoriaJaExiste = categories.some(
      (c) => c.descricao.toLowerCase() === descricaoNormalizada
    );
    if (categoriaJaExiste) {
      toast.error(t('categoryAlreadyExists'));
      return;
    }

    try {
      setLoading(true);
      const created = await createCustomCategory({
        userId,
        descricao: descricaoOriginal,
      });
      setCategories((prev) => [...prev, created]);
      setSelected('');
      setNewText('');
      setShowAdd(false);
      toast.success(t('categorySavedSuccess'));
    } catch (err) {
      console.error(err);
      toast.error(t('errorSavingCategory'));
    } finally {
      setLoading(false);
    }
  }

  async function onExcluir(categoryId) {
    try {
      await deleteCustomCategory(categoryId, userId);
      setCategories((prev) => prev.filter((c) => c._id !== categoryId));
      setSelected('');
      toast.success(t('categoryDeletedSuccess'));
    } catch (err) {
      console.error(err);
      toast.error(t('errorDeletingCategory'));
    }
  }

  return (
    <div className="space-y-6">
      {!showAdd ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_auto] md:items-end">
          <div className="space-y-2">
            <Label>{t('existingCategories')}</Label>
            <Select
              value={selected || undefined}
              onValueChange={setSelected}
              disabled={loading || categories.length === 0}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    categories.length === 0
                      ? t('selectCategory')
                      : t('selectCategory')
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c._id} value={c._id}>
                    {c.descricao}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-wrap gap-2">
            {selected && (
              <Button
                variant="outline"
                onClick={() => onExcluir(selected)}
                disabled={loading}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t('deleteCategory')}
              </Button>
            )}
            <Button onClick={() => setShowAdd(true)} disabled={loading}>
              <Plus className="w-4 h-4 mr-2" />
              {t('newCategory')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t('newCategory')}</Label>
            <Input
              autoFocus
              placeholder={t('newCategoryDescriptionPlaceholder')}
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSalvar();
                if (e.key === 'Escape') onCancelar();
              }}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onCancelar} disabled={loading}>
              {t('cancel')}
            </Button>
            <Button onClick={onSalvar} disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              {t('save')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
